import React from "react";
import logo from "../../images/TestAssessify.png";
import errorIcon from "../../components/customtoast/icons/error.png";
import toast, { LoaderIcon } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation } from "react-query";
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from "formik";
import * as Yup from "yup";
import { api } from "../../api";
import { useCustomToast } from "../../components/customtoast/CustomToastProvider";
import { startSurveySession } from "../../utils/surveySession";

interface CultureAuditLoginValues {
  access_code: string;
}

// Culture audit's magic-link login. Unlike CheckProfile.tsx (360/appraisal:
// email lookup -> profile selection -> user_exam_id-based /auth/audit/login),
// a single access_code identifies the UserExam directly, so there's no
// intermediate profile-selection step here.
function CultureAuditLogin(): JSX.Element {
  const navigate = useNavigate();
  const { access_code: accessCodeParam }: any = useParams();
  const customToast: any = useCustomToast();
  const attemptedAutoLogin = React.useRef(false);
  // Set when the link itself fails; the page then shows only this error, never
  // the TestAssessify code form (clients don't want TestAssessify shown).
  const [linkError, setLinkError] = React.useState<string | null>(null);

  const { mutateAsync: onMagicLogin, isLoading } = useMutation(api.surveyMagicLogin);

  const completeLogin = async (access_code: string, fromLink = false) => {
    try {
      toast.loading("Logging in...");
      const data = await onMagicLogin({ access_code });
      toast.remove();

      await startSurveySession(data?.data, access_code);

      // Culture audit has no dashboard/reviewer concept — go straight to the
      // surveys list (CandidateRoutes.jsx also hides the sidebar/header chrome
      // for this sub_type, and guards a direct /dashboard visit back here).
      navigate("/candidate/self-review");
    } catch (error: any) {
      toast.remove();
      // No response at all means the server wasn't reached, not a bad link.
      const message = !error?.response
        ? "We couldn't connect to the survey. Please check your internet connection and try again."
        : error.response.data?.message ||
          "Invalid or expired link. Please check your code and try again.";
      if (fromLink) {
        setLinkError(message);
      } else {
        customToast.error(message);
      }
    }
  };

  // Auto-login when the code arrives via the link itself (/survey-login/:access_code).
  React.useEffect(() => {
    if (accessCodeParam && !attemptedAutoLogin.current) {
      attemptedAutoLogin.current = true;
      completeLogin(accessCodeParam, true);
    }
  }, [accessCodeParam]);

  const validationSchema = Yup.object({
    access_code: Yup.string().required("Please enter your access code"),
  });

  const onSubmit = async (
    values: CultureAuditLoginValues,
    { setSubmitting }: FormikHelpers<CultureAuditLoginValues>
  ): Promise<void> => {
    setSubmitting(true);
    await completeLogin(values.access_code.trim());
    setSubmitting(false);
  };

  const retryLink = () => {
    setLinkError(null);
    completeLogin(accessCodeParam, true);
  };

  // A link that fails shows just the error over a blank page — no
  // TestAssessify branding, and no Dismiss since there's nothing behind it.
  if (accessCodeParam && linkError) {
    return (
      <div className="bg-white min-h-screen p-6 flex justify-center items-center">
        <div
          role="alertdialog"
          aria-labelledby="link-error-title"
          aria-describedby="link-error-message"
          className="flex flex-col items-center justify-center gap-2 rounded-lg w-full mx-auto p-7 bg-white shadow-2xl"
          style={{ maxWidth: "500px" }}
        >
          <div className="w-full p-2 flex items-center justify-center">
            <img src={errorIcon} alt="" className="w-36 h-36" />
          </div>
          <h1 id="link-error-title" className="text-center text-slate-700 font-normal text-2xl">
            Error!
          </h1>
          <p id="link-error-message" className="text-center text-slate-600">
            {linkError}
          </p>
          <p className="text-center text-slate-500 text-sm mt-2">
            Please open the link from your most recent survey invitation. If this keeps happening,
            contact your survey administrator.
          </p>
          <button
            type="button"
            onClick={retryLink}
            className="w-full mt-4 rounded-md text-baseFont bg-customOrange text-white py-2"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // While a link is being checked we don't know the client yet, so show a
  // neutral screen — the TestAssessify card would flash before the client's
  // branding takes over on the surveys page.
  if (accessCodeParam) {
    return (
      <div className="bg-white h-screen flex flex-col items-center justify-center">
        <LoaderIcon />
        <p className="mt-4 text-slate-500">Loading your survey...</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-4 lg:p-6">
      <div className="flex justify-center items-center mt-20 2xl:mt-0 2xl:h-screen">
        <div className="bg-cover bg-no-repeat bg-hero-img w-full md:w-[600px] lg:w-[900px] 2xl:w-[1440px] 2xl:h-[800px] rounded-xl p-2 lg:p-10 sm:shadow-2xl">
          <div className="my-2 shadow-2xl rounded-[8px] bg-white w-full lg:w-1/2 2xl:h-[600px] 2xl:mt-28 p-4 2xl:p-6">
            <div className="flex flex-col w-full">
              <div className="flex flex-col justify-center pb-8 lg:px-4 pt-8 my-auto md:justify-start md:pt-0 2xl:px-10">
                <div className="h-16 mb-6">
                  <img src={logo} alt="Test Assessify" className="h-full object-contain" />
                </div>

                <Formik<CultureAuditLoginValues>
                  initialValues={{ access_code: "" }}
                  validationSchema={validationSchema}
                  onSubmit={onSubmit}
                >
                  {() => (
                    <Form className="flex flex-col pt-3 md:pt-8">
                      <h1 className="text-3xl font-medium text-darkBlue mb-2">
                        Enter your access code
                      </h1>
                      <p className="mb-6 text-slate-500">
                        Use the code from your invitation email or link.
                      </p>
                      <Field
                        type="text"
                        id="access_code"
                        name="access_code"
                        placeholder="e.g. PIP-1007-482913"
                        className="flex-1 appearance-none rounded-lg border border-gray-300 w-full py-4 px-4 bg-white text-gray-700 placeholder-gray-400 shadow-sm text-base focus:outline-none focus:ring-2 focus:ring-[#88a848] focus:border-transparent mb-2"
                      />
                      <ErrorMessage
                        name="access_code"
                        component="div"
                        className="text-red-500 text-sm mb-4"
                      />
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full mt-3 px-4 py-3 rounded-lg text-base font-semibold text-center text-white transition duration-200 ease-in bg-[#88a848] shadow-md focus:outline-none focus:ring-2"
                      >
                        {isLoading ? (
                          <div className="w-full flex justify-center">
                            <LoaderIcon />
                          </div>
                        ) : (
                          "Continue"
                        )}
                      </button>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CultureAuditLogin;
